import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import type { IdentityFlow } from '@identity/domain/types';

const IdentityFlowContext = createContext<IdentityFlow | null>(null);

export type IdentityFlowProviderProps = {
  /** The journey mounted below. It is fixed by the route and never changes. */
  flow: IdentityFlow;
  children: ReactNode;
};

/**
 * Declares which flow is mounted below, and with it the credential the composition authorizes its
 * calls with.
 *
 * It is not wizard state: the value is fixed by the route and never changes while mounted. It is a
 * context and not a prop because the hooks that need it live several levels below, inside steps that
 * must not know which flow they belong to.
 *
 * @param {IdentityFlowProviderProps} props - Component properties
 */
export const IdentityFlowProvider = ({ children, flow }: IdentityFlowProviderProps) => (
  <IdentityFlowContext.Provider value={flow}>{children}</IdentityFlowContext.Provider>
);

/**
 * The flow the current screen belongs to. There is no default on purpose: one would make every new
 * screen someone else's flow silently, failing as a `401` in production instead of in a test.
 */
export const useIdentityFlow = (): IdentityFlow => {
  const flow = useContext(IdentityFlowContext);

  if (flow === null) {
    throw new Error(
      '[Identity] No flow declared.\n' +
        'Wrap the screen in <IdentityFlowProvider flow="invitation" | "verifyAccount" | "recovery" | "profile">.',
    );
  }

  return flow;
};
